import { cancellationApiService } from '@/services';
import useCancellationOrders from '@/hooks/use-cancellation-orders';
import {
  Box,
  Alert,
  Button,
  Dialog,
  Divider,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { CancelMilestoneItem } from './cancel-milestone-item';
import {
  CancellationOrderResponseDto,
  CancellationMilestoneWithAction,
} from '@/types/cancellation';
import { CancellationMode, CancellableItemsResponseDto } from '@/services/cancellation.service';

type DialogCancelMilestonesProps = {
  open: boolean;
  onClose: () => void;
  orderId: string;
  cancellationId?: string;
  defaultMilestones?: CancellationMilestoneWithAction[];
  mode?: CancellationMode;
};

type CancellationFormValues = {
  items: {
    [orderItemId: string]: string[];
  };
};

export const DialogCancelMilestones = ({
  open,
  onClose,
  orderId,
  cancellationId,
  defaultMilestones = [],
  mode = CancellationMode.CREATE,
}: DialogCancelMilestonesProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: organizationId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, error } = useQuery<CancellableItemsResponseDto>({
    queryKey: ['cancellation-milestones', orderId],
    queryFn: () => cancellationApiService.getItemsForCancellation(orderId),
    enabled: !!orderId && open,
    refetchOnWindowFocus: false,
  });

  const processedDataRef = useRef<string>('');

  const methods = useForm<CancellationFormValues>({
    defaultValues: { items: {} },
  });

  const { watch, setValue, handleSubmit, reset } = methods;
  const values = watch();

  const mapMilestonesForEdit = useCallback(() => {
    if (!data || !defaultMilestones.length) return {};

    const mapped: Record<string, string[]> = {};
    defaultMilestones.forEach((cancelledMilestone) => {
      const { orderItemId } = cancelledMilestone;
      if (!orderItemId) return;

      const cancellableItem = data.items.find((item) => item._id === orderItemId);
      if (!cancellableItem) return;

      const matchingMilestone = cancellableItem.milestones.find((milestone) => {
        const isSameDate =
          new Date(milestone.date).toISOString() ===
          new Date(cancelledMilestone.date).toISOString();
        const isSamePeriod = milestone.period === cancelledMilestone.period;
        return isSameDate && isSamePeriod;
      });

      if (matchingMilestone) {
        if (!mapped[orderItemId]) mapped[orderItemId] = [];
        mapped[orderItemId].push(matchingMilestone._id);
      }
    });

    return mapped;
  }, [data, defaultMilestones]);

  useEffect(() => {
    if (!open) {
      processedDataRef.current = '';
      return;
    }

    const dataKey = `${mode}-${defaultMilestones.length}-${data?.items?.length || 0}-${orderId}`;
    if (processedDataRef.current === dataKey) return;

    const formData = mode === CancellationMode.EDIT ? mapMilestonesForEdit() : {};
    reset({ items: formData });
    processedDataRef.current = dataKey;
  }, [open, mode, defaultMilestones, data, orderId, reset, mapMilestonesForEdit]);

  const { createCancellation, updateCancellation, isCreating, isUpdating } = useCancellationOrders({
    organizationId,
    onSuccess: (response: CancellationOrderResponseDto) => {
      queryClient.invalidateQueries(['cancellation-order', cancellationId]);
      onClose();
      navigate(
        `/management/accounts/organizations/${organizationId}/cancellation-orders/${response._id}`,
        { state: { updated: !!cancellationId, draftCreated: !cancellationId } }
      );
    },
  });

  // Handlers
  const toggleMilestone = (orderItemId: string, milestoneId: string, selected: boolean) => {
    const current = values.items[orderItemId] || [];
    const updated = selected
      ? Array.from(new Set([...current, milestoneId]))
      : current.filter((id) => id !== milestoneId);

    setValue(`items.${orderItemId}`, updated, { shouldDirty: true });
  };

  const toggleAllForItem = (orderItemId: string, allMilestoneIds: string[]) => {
    const current = values.items[orderItemId] || [];
    const allSelected =
      current.length === allMilestoneIds.length &&
      allMilestoneIds.every((id) => current.includes(id));

    setValue(`items.${orderItemId}`, allSelected ? [] : allMilestoneIds, { shouldDirty: true });
  };

  const onSubmit = async (formValues: CancellationFormValues) => {
    const items = Object.entries(formValues.items)
      .filter(([_, milestoneIds]) => milestoneIds.length > 0)
      .map(([orderItemId, milestoneIds]) => ({
        orderItemId,
        milestoneIds,
      }));

    if (!items.length) {
      enqueueSnackbar(t('Please select at least one milestone to cancel'), {
        variant: 'warning',
      });
      return;
    }

    try {
      if (cancellationId) {
        await updateCancellation(cancellationId, { items, orderId });
      } else {
        await createCancellation({ orderId, items });
      }
    } catch (err) {
      console.error('Error in cancellation submission:', err);
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle sx={{ padding: 0 }}>
        <Box p="35px 35px 16px 35px" display="flex" flexDirection="column" gap="8px">
          <Typography fontSize="25px" fontWeight={700} lineHeight="24px">
            {t('Order')} N° {data?.orderNumber}
          </Typography>
          <Typography fontSize="16px" fontWeight={400} lineHeight="24px">
            {t('Select the billing milestones you want to cancel:')}
          </Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ pt: '21px' }}>
            {isLoading && (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            )}

            {!!error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {t('Error loading cancellation data')}
              </Alert>
            )}

            {data && !isLoading && (
              <Box height="386px" overflow="auto" display="flex" flexDirection="column" gap="20px">
                {data.items.map((item) => {
                  const orderItemId = item._id;
                  const selectedMilestoneIds = values.items[orderItemId] || [];
                  const allMilestoneIds = item.milestones.map((m) => m._id);

                  return (
                    <CancelMilestoneItem
                      key={orderItemId}
                      item={item}
                      selectedMilestones={selectedMilestoneIds}
                      onMilestoneSelect={(milestoneId, selected) =>
                        toggleMilestone(orderItemId, milestoneId, selected)
                      }
                      onSelectAll={() => toggleAllForItem(orderItemId, allMilestoneIds)}
                    />
                  );
                })}
              </Box>
            )}
          </DialogContent>

          <Divider />

          <DialogActions sx={{ pt: '24px', pb: '25px' }}>
            <Button onClick={onClose} sx={{ minWidth: '147px', borderRadius: '8px' }}>
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isCreating || isUpdating}
              sx={{ minWidth: '147px', borderRadius: '8px' }}
            >
              {isCreating || isUpdating
                ? t('Processing...')
                : cancellationId
                  ? t('Update Cancellation')
                  : t('Confirm Cancellation')}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
